import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from 'lucide-react';
import { useChannels } from '../context/CategoriesContext';
import { iconFor } from '../lib/icons';
import { useFitScale } from '../hooks/useFitScale';

// Menu is laid out at a fixed "design" size, then uniformly scaled to fit the screen.
const FULL_W = 900;
const COMPACT_W = 520;

export default function Home() {
  const navigate = useNavigate();
  const inputs = useChannels()
    .filter((c) => c.key !== 'home')
    .map((c, i) => ({ ...c, num: String(i + 1).padStart(2, '0'), Icon: c.key === 'about' ? User : iconFor(c.icon) }));
  const [selected, setSelected] = useState(0);
  const [entering, setEntering] = useState(false);
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const { scale, compact, ready } = useFitScale(outerRef, innerRef);

  const enter = useCallback((idx) => {
    setEntering(true);
    setTimeout(() => navigate(inputs[idx].path), 260);
  }, [navigate, inputs]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelected((s) => (s + 1) % inputs.length); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setSelected((s) => (s - 1 + inputs.length) % inputs.length); }
      else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); enter(selected); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, enter, inputs.length]);

  return (
    <div ref={outerRef} className={`page-in relative w-full h-full overflow-hidden ${entering ? 'menu-exit' : ''}`} data-testid="home-menu">
      <div
        ref={innerRef}
        className="absolute left-1/2 top-1/2 flex flex-col"
        style={{
          width: 'max-content',
          minWidth: compact ? COMPACT_W : FULL_W,
          gap: 36,
          transform: `translate(-50%, -50%) scale(${scale})`,
          visibility: ready ? 'visible' : 'hidden'
        }}
      >
        <h1 className="font-display text-[#ffe9b8] leading-none phosphor-glow" style={{ fontSize: 72 }}>
          INPUT SELECT
        </h1>

        <ul role="menu" className="flex flex-col" style={{ gap: 14 }}>
          {inputs.map((item, i) => (
            <MenuRow key={item.path} item={item} active={i === selected} compact={compact}
                     onHover={() => setSelected(i)} onClick={() => enter(i)} />
          ))}
        </ul>

        <div className="flex flex-wrap items-center font-osd tracking-widest text-[#a88552]" style={{ gap: '8px 32px', fontSize: 17 }}>
          <span><span className="text-[#ffc968]">▲ ▼</span>&nbsp; NAVIGATE</span>
          <span><span className="text-[#ffc968]">OK / ⏎</span>&nbsp; SELECT</span>
          {!compact && <span><span className="text-[#ffc968]">ESC</span>&nbsp; EXIT</span>}
          <span className="ml-auto text-[#ffb84d]" data-testid="home-channel-counter">
            CH {String(selected + 1).padStart(2, '0')} / {String(inputs.length).padStart(2, '0')}
          </span>
        </div>
      </div>
    </div>
  );
}

function MenuRow({ item, active, compact, onHover, onClick }) {
  const { Icon } = item;
  return (
    <li role="menuitem">
      <button
        onMouseEnter={onHover}
        onFocus={onHover}
        onClick={onClick}
        data-testid={`home-menu-item-${item.key}`}
        className="w-full group flex items-baseline text-left"
        style={{ gap: 28, paddingTop: 4, paddingBottom: 4 }}
      >
        <span className={`font-osd shrink-0 transition-opacity ${active ? 'text-[#ffc968] opacity-100' : 'text-[#c9a96e] opacity-30'}`}
              style={{ fontSize: 28, width: '1ch' }}>
          {active ? '▸' : ' '}
        </span>
        {!compact && (
          <span className={`font-osd tracking-[0.3em] shrink-0 ${active ? 'text-[#ffb84d]' : 'text-[#a88552]'}`}
                style={{ fontSize: 18, width: 140 }}>
            INPUT {item.num}
          </span>
        )}
        <Icon className={`shrink-0 self-center ${active ? 'text-[#ffc968]' : 'text-[#8b6a3e]'}`} style={{ width: 26, height: 26 }} />
        <span className="flex-1 min-w-0">
          <span className={`tv-blink ${active ? 'is-active' : ''} block font-display leading-none whitespace-nowrap ${active ? 'text-[#ffe9b8]' : 'text-[#d8b887] group-hover:text-[#ffe9b8]'} transition-colors`}
                style={{ fontSize: 48 }}>
            {item.name}
          </span>
        </span>
        {!compact && (
          <span className={`font-osd tracking-widest shrink-0 text-right ${active ? 'text-[#ffc968]' : 'text-[#5c3f18]'}`}
                style={{ fontSize: 18, width: 150 }}>
            {active ? 'PRESS OK ▸' : ''}
          </span>
        )}
      </button>
    </li>
  );
}
